












































import { Component, Prop, Mixins } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import _ from "@/boot/lodash";

// Mixins
import HasValidation from "@/mixins/HasValidation";

// Enums/Data
import { DiscountTypes } from "@/data/enums/promotions";
import { InvoiceCategoryCode } from "@/models/invoices";
import { ConditionTypes } from "@/data/enums/affiliates";
import { FilterOperators } from "@/data/table";

// Types
import {
  IAffiliateTier,
  IAffiliateTierCondition,
  IAffiliateTierConditionForm
} from "@/models/affiliate";
import { ISelectedProduct } from "@/models/products";

@Component({
  components: {
    "affiliate-tier-condition-provider": () =>
      import(
        "@/components/app/admin/settings/affiliates/conditions/affiliateTierConditionProvider.vue"
      ),
    "affiliate-tier-condition-fields": () =>
      import(
        "@/components/app/admin/settings/affiliates/conditions/affiliateTierConditionFields.vue"
      )
  }
})
export default class AddEditAffiliateTierConditionModal extends Mixins(
  HasValidation
) {
  // Refs

  public declare $refs: {
    affiliateTierConditionProvider: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Props

  @Prop({ required: true })
  public affiliateTierId!: IAffiliateTier["id"];

  @Prop({ required: true })
  public affiliateTierConditionId!: IAffiliateTierCondition["id"];

  @Prop({ required: true })
  public affiliateTierCondition!: IAffiliateTierCondition;

  // Data
  public form: IAffiliateTierConditionForm = {
    active: true,
    type: DiscountTypes.PERCENTAGE_BASED,
    amount: 0,
    billing_cycle_months: FilterOperators.ALL,
    commission_period: 6,
    payout_interval: 60,
    invoice_categories: [InvoiceCategoryCode.NEW_CONTRACT],
    condition_type: ConditionTypes.GLOBAL,
    apply_multiple_products: true,
    commission_once_per_referral: true,
    product_price_order: 0
  };
  public formClone = {};
  public selectedProducts: ISelectedProduct[] = [];

  public isProcessing = false;

  // Methods

  public created() {
    this.selectedProducts = _.map(
      _.get(this.affiliateTierCondition, "products", []),
      selectedProduct => _.pick(selectedProduct, ["id", "category_id", "name"])
    );
  }

  public initiateForm() {
    this.formClone = _.cloneDeep(this.form);
  }

  public async validateBeforeSubmit() {
    if (!this.canSave) return;

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      if (this.isNewTier) {
        this.$emit("success", {
          ...this.formForSend,
          id: this.isNewCondition
            ? this.$uuid()
            : this.affiliateTierCondition.id,
          products: this.isSelectedProducts ? this.selectedProducts : []
        });
        this.$emit("close");
        return;
      }

      this.isProcessing = true;

      if (this.isNewCondition) {
        await this.createTierCondition();
      } else {
        await this.updateTierCondition();
        await this.updateTierConditionProducts();
      }

      this.showManageConditionMessage();
      this.$emit("success");
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public createTierCondition() {
    return this.$refs.affiliateTierConditionProvider.createTierCondition(
      _.merge(
        {},
        this.formForSend,
        this.isSelectedProducts && this.selectedProductsAreChanged
          ? {
              product_ids: this.selectedProductIds
            }
          : {}
      )
    );
  }

  public updateTierCondition() {
    return this.$refs.affiliateTierConditionProvider.updateTierCondition(
      this.formForSend
    );
  }

  public updateTierConditionProducts() {
    if (!this.isSelectedProducts || !this.selectedProductsAreChanged)
      return Promise.resolve();

    const productIds = _.map(
      _.get(this.affiliateTierCondition, "products", []),
      product => product.id
    );

    const newProductIds = _.differenceWith(
      this.selectedProductIds,
      productIds,
      _.isEqual
    );

    const oldProductIds = _.differenceWith(
      productIds,
      this.selectedProductIds,
      _.isEqual
    );

    return Promise.all([
      newProductIds.length
        ? this.$refs.affiliateTierConditionProvider.attachSelectedProducts(
            newProductIds
          )
        : Promise.resolve(),
      oldProductIds.length
        ? this.$refs.affiliateTierConditionProvider.detachSelectedProducts(
            oldProductIds
          )
        : Promise.resolve()
    ]);
  }

  public async updateSelectedProducts(newSelectedProducts) {
    this.$set(this, "selectedProducts", newSelectedProducts);
  }

  public showManageConditionMessage() {
    this.$store.dispatch("toast/show", {
      message: this.isNewCondition
        ? this.$t("_.affiliate_tier_condition_successfully_updated")
        : this.$t("_.affiliate_tier_condition_successfully_created")
    });
  }

  // Computed

  public get isNewTier() {
    return _.isNil(this.affiliateTierId);
  }

  public get isNewCondition() {
    return _.isNil(this.affiliateTierConditionId);
  }

  public get title() {
    return this.isNewCondition
      ? this.$t("_.add_affiliate_tier_condition")
      : this.$t("_.update_affiliate_tier_condition");
  }

  public get canSave() {
    return this.isNewCondition
      ? this.$userCan("create_affiliate_tier_condition")
      : this.$userCan("update_affiliate_tier_condition");
  }

  public get formIsChanged() {
    return (
      !this.$_.isEqual(this.form, this.formClone) ||
      this.selectedProductsAreChanged
    );
  }

  public get selectedProductsAreChanged() {
    return !this.$_.isEqual(
      _.sortBy(
        _.map(this.selectedProducts, selectedProduct => selectedProduct.id)
      ),
      _.sortBy(
        _.map(
          _.get(this.affiliateTierCondition, "products", []),
          selectedProduct => selectedProduct.id
        )
      )
    );
  }

  public get formForSend() {
    const form = this.form;

    if (this.form.billing_cycle_months === FilterOperators.ALL)
      form.billing_cycle_months = null;

    return form;
  }

  public get isSelectedProducts() {
    return this.form.condition_type === ConditionTypes.SELECTED_PRODUCTS;
  }

  public get selectedProductIds() {
    return _.map(this.selectedProducts, product => product.id);
  }
}
